import * as Dialog from '@radix-ui/react-dialog';
import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';

type AvailableTimeslot = { id: string; startTime: Date; endTime: Date };
type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCancelAll: () => void; // closes everything
  headerHeight?: number; // defaults to 81px
  windows?: { id: string; startTime: string; endTime: string }[]; // optional API feed (ISO)
};

const Overlay = styled(Dialog.Overlay)`
  position: fixed;
  top: 81px; /* below header */
  left: 0;
  right: 0;
  bottom: 0;
  @media (min-width: 768px) { left: var(--sidebar-width, 0px); }
  background: rgba(0, 0, 0, 0.35);
  z-index: 1100;
`;

const Panel = styled(Dialog.Content, {
  shouldForwardProp: (prop) => prop !== '$top',
})<{ $top: number }>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: 0;
  right: 0;
  bottom: 0;
  @media (min-width: 768px) { left: var(--sidebar-width, 0px); }
  background: #1a1a1c;
  z-index: 1101;
  display: grid;
  grid-template-rows: auto auto 1px auto auto 1fr auto auto; /* + safe-area */
  overflow: hidden;
`;

const Hero = styled.div`
  width: 374px;
  max-width: 100%;
  margin: 0 auto;
  padding: 12px 16px;
`;

const Title = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 21px;
  line-height: 140%;
  color: #e9ecf5;
`;

const Subtitle = styled.div`
  margin-top: 4px;
  font-family: Inter;
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
  color: #b7bdcb;
`;

const BarBase = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr 32px;
  align-items: center;
  width: 374px;
  max-width: 100%;
  margin: 0 auto;
  padding: 8px 16px;
  gap: 8px;
`;

/* Plain chevron: bigger (18px) */
const Chevron = styled.button<{ disabled?: boolean }>`
  background: transparent;
  border: 0;
  color: ${({ disabled }) => (disabled ? '#4a4f5a' : '#b7bdcb')};
  font-size: 18px; /* ⬅️ 18px size */
  line-height: 18px; /* ⬅️ keep the glyph exactly 18px tall */
  height: 18px; /* ⬅️ height requirement */
  display: grid;
  place-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const MonthBar = BarBase;
const DaysBar = BarBase;

const MonthText = styled.div`
  text-align: center;
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  color: #e9ecf5;
`;

const Divider = styled.div`
  height: 1px;
  background: #2b2d33;
  width: 100%;
`;

const WeekRow = styled.div`
  width: 375px;
  max-width: 100%;
  margin: 0 auto;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
`;

/* Column that holds the day card and (optional) mini times */
const DayCol = styled.div`
  display: grid;
  gap: 6px;
  justify-items: center;
`;

const DayCard = styled('button', {
  shouldForwardProp: (prop) => prop !== '$selected' && prop !== '$disabled',
})<{ $selected?: boolean; $disabled?: boolean }>`
  width: 43px;
  min-height: 62px; /* allow to grow if needed */
  border-radius: 8px;
  padding: 10px;
  border: 1px solid ${({ $selected }) => ($selected ? '#7B8CFF' : '#2B2D33')};
  background: ${({ $selected }) => ($selected ? '#7B8CFF' : 'transparent')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: grid;
  gap: 4px;
  place-items: center;
`;

const DayName = styled('div', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: ${({ $selected }) => ($selected ? '#0C0D10' : '#B7BDCB')};
`;

const DayNum = styled('div', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 600;
  font-size: 15px;
  line-height: 140%;
  color: ${({ $selected }) => ($selected ? '#0C0D10' : '#E9ECF5')};
`;

const TZNote = styled.div`
  margin: 0 auto;
  width: 374px;
  max-width: 100%;
  padding: 0 16px 12px;
  text-align: center;
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 140%;
  color: #b7bdcb;
`;

const TimesScroll = styled.div`
  overflow: auto;
  padding: 0 16px 12px;
`;

const TimesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  max-width: 374px;
  margin: 0 auto;
`;

const TimeBtn = styled('button', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected?: boolean }>`
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${({ $selected }) => ($selected ? '#7B8CFF' : '#242A33')};
  background: ${({ $selected }) => ($selected ? '#7B8CFF' : '#171A1F')};
  color: ${({ $selected }) => ($selected ? '#0C0D10' : '#E8EAF1')};
  font-size: 12px;
  cursor: pointer;
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  /* add extra 40px + safe-area */
  padding: 12px 16px calc(12px + 40px + env(safe-area-inset-bottom, 0));
  display: grid;
  gap: 10px;
`;

/* add 40px on top of safe-area */
const SafeAreaBottom = styled.div`
  height: calc(40px + env(safe-area-inset-bottom, 0));
`;

const Hint = styled.div`
  text-align: center;
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 140%;
  color: #b7bdcb;
`;

const ConfirmBtn = styled.button<{ disabled?: boolean }>`
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: 0.2px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled }) => (disabled ? '#23252A' : '#7B8CFF')};
  color: ${({ disabled }) => (disabled ? '#6E7481' : '#0C0D10')};
`;

const CancelBtn = styled.button`
  width: 100%;
  height: 32px;
  background: transparent;
  border: 0;
  font-family: Inter;
  font-weight: 600;
  font-size: 15px;
  color: #b7bdcb;
  cursor: pointer;
`;

/** helpers */
const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const startOfWeekSun = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // Sun=0
  x.setDate(x.getDate() - day);
  return x;
};
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const monthName = (d: Date) => d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
const dowShort = (d: Date) => d.toLocaleString(undefined, { weekday: 'short' });

function weekRangeText(start: Date) {
  const end = addDays(start, 6);
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const s = `${start.toLocaleString(undefined, { month: 'short' })} ${start.getDate()}`;
  const e = sameMonth
    ? `${end.getDate()}`
    : `${end.toLocaleString(undefined, { month: 'short' })} ${end.getDate()}`;
  const y =
    start.getFullYear() === end.getFullYear()
      ? `${start.getFullYear()}`
      : `${start.getFullYear()}–${end.getFullYear()}`;
  return `${s}–${e}, ${y}`;
}

// NEW: only produce slots at/after "now" for the requested date
function makeSlotsForDay(
  windows: { startTime: Date; endTime: Date }[],
  date: Date,
  durationMin = 30,
  stepMin = 30,
  now = new Date()
) {
  const out: Date[] = [];
  windows.forEach(({ startTime, endTime }) => {
    if (!sameDay(startTime, date)) return;
    for (let t = +startTime; t + durationMin * 60000 <= +endTime; t += stepMin * 60000) {
      if (t >= +now) out.push(new Date(t)); // NEW: trim past
    }
  });
  return out;
}

/* ---- mock availability if no API feed is given ---- */
/* NEW: many more future days + richer blocks, including Tu/Th and a Saturday */
function generateMockWindows(base = new Date()): AvailableTimeslot[] {
  const dayMs = 24 * 60 * 60 * 1000;
  const weekdayBlocks = [
    { start: '09:00', end: '12:00' },
    { start: '13:00', end: '15:00' },
    { start: '16:30', end: '19:00' },
  ];
  const satBlocks = [
    { start: '10:00', end: '13:00' },
    { start: '14:00', end: '16:00' },
  ];
  let id = 1;
  const nextNDays = 60; // was 14

  const out: AvailableTimeslot[] = [];
  for (let i = 0; i < nextNDays; i++) {
    const d = new Date(+base + i * dayMs);
    const dow = d.getDay(); // 0 Sun ... 6 Sat

    // Available on Mon-Fri (heavier), and some Saturdays
    if (dow >= 1 && dow <= 5) {
      weekdayBlocks.forEach(({ start, end }) => {
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        const s = new Date(d);
        s.setHours(sh, sm, 0, 0);
        const e = new Date(d);
        e.setHours(eh, em, 0, 0);
        out.push({ id: `mock-${id++}`, startTime: s, endTime: e });
      });
    } else if (dow === 6 && i % 2 === 0) {
      // every other Saturday
      satBlocks.forEach(({ start, end }) => {
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        const s = new Date(d);
        s.setHours(sh, sm, 0, 0);
        const e = new Date(d);
        e.setHours(eh, em, 0, 0);
        out.push({ id: `mock-${id++}`, startTime: s, endTime: e });
      });
    }
  }
  return out;
}

export default function ScheduleMeetingModal({
  open,
  onOpenChange,
  onCancelAll,
  headerHeight = 81,
  windows,
}: Props) {
  // parse availability
  const [available, setAvailable] = useState<AvailableTimeslot[]>([]);
  // NEW: "now" frozen for the render session so it doesn't move during interaction
  const now = useMemo(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return d;
  }, []);

  useEffect(() => {
    if (!windows) {
      // use mock data
      setAvailable(generateMockWindows(now));
      return;
    }
    const parsed = windows.map((w) => ({
      id: w.id,
      startTime: new Date(w.startTime),
      endTime: new Date(w.endTime),
    }));
    setAvailable(parsed);
  }, [windows, now]);

  // NEW: future-only availability (respect "no past")
  const availableFuture = useMemo(
    () => available.filter((w) => w.endTime >= now),
    [available, now]
  );

  // viewed month/week anchor — start on the first future-available week (or this week)
  const firstFutureDate = useMemo(
    () => (availableFuture.length ? availableFuture[0].startTime : now),
    [availableFuture, now]
  );
  const [viewDate, setViewDate] = useState<Date>(
    new Date(firstFutureDate.getFullYear(), firstFutureDate.getMonth(), 1)
  );

  // the active week (Sunday → Saturday) shown as 7 cards
  const weekStart = useMemo(() => startOfWeekSun(viewDate), [viewDate]);
  const days = [...Array(7)].map((_, i) => addDays(weekStart, i));

  // NEW: compute week anchors that have at least one future slot
  const weekAnchors = useMemo(() => {
    const set = new Set<number>();
    availableFuture.forEach((w) => set.add(+startOfWeekSun(w.startTime)));
    // ensure current week if it has future slots today+
    const todayWeek = +startOfWeekSun(now);
    const todayHasFuture = availableFuture.some((w) => +startOfWeekSun(w.startTime) === todayWeek);
    if (todayHasFuture) set.add(todayWeek);
    return Array.from(set).sort((a, b) => a - b);
  }, [availableFuture, now]);

  const currentAnchor = +startOfWeekSun(weekStart);
  const prevAnchor = useMemo(
    () => weekAnchors.filter((t) => t < currentAnchor).slice(-1)[0],
    [weekAnchors, currentAnchor]
  );
  const nextAnchor = useMemo(
    () => weekAnchors.find((t) => t > currentAnchor),
    [weekAnchors, currentAnchor]
  );

  // day + time selection
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStart, setSelectedStart] = useState<Date | null>(null);

  useEffect(() => {
    setSelectedDate(null);
    setSelectedStart(null);
  }, [viewDate]);

  // NEW: day has future windows only
  const hasWindows = (d: Date) =>
    availableFuture.some((w) => sameDay(w.startTime, d) && w.endTime >= now);

  const daySlots = useMemo(
    () => (selectedDate ? makeSlotsForDay(availableFuture, selectedDate, 30, 30, now) : []),
    [availableFuture, selectedDate, now]
  );

  const tzLabel = useMemo(() => {
    try {
      return (
        new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
          .formatToParts(new Date())
          .find((p) => p.type === 'timeZoneName')?.value || 'Local'
      );
    } catch {
      return 'Local';
    }
  }, []);

  // NEW: helpers to navigate only to weeks that have windows (no past)
  const gotoPrevAvailableWeek = () => {
    if (!prevAnchor) return;
    setViewDate(new Date(prevAnchor));
  };
  const gotoNextAvailableWeek = () => {
    if (!nextAnchor) return;
    setViewDate(new Date(nextAnchor));
  };

  // NEW: Month bar chevrons repurposed to jump between available weeks too
  const canGoPrev = Boolean(prevAnchor);
  const canGoNext = Boolean(nextAnchor);

  // NEW: disable selecting past days in current week (UI safety)
  const isPastDay = (d: Date) =>
    d < new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Overlay />
      <Panel $top={headerHeight} aria-describedby={undefined}>
        {/* Hero */}
        <Hero>
          <Dialog.Title asChild>
            <Title>Schedule a Meeting</Title>
          </Dialog.Title>
          <Subtitle>Choose a time that works for you.</Subtitle>
        </Hero>

        {/* Month switch (now jumps between available weeks) */}
        <MonthBar>
          <Chevron
            aria-label="Previous available week"
            onClick={gotoPrevAvailableWeek}
            disabled={!canGoPrev}
          >
            ‹
          </Chevron>
          <MonthText>{monthName(weekStart)}</MonthText>
          <Chevron
            aria-label="Next available week"
            onClick={gotoNextAvailableWeek}
            disabled={!canGoNext}
          >
            ›
          </Chevron>
        </MonthBar>

        <Divider />

        {/* Days (week) switch (also jumps between available weeks) */}
        <DaysBar>
          <Chevron
            aria-label="Previous available week"
            onClick={gotoPrevAvailableWeek}
            disabled={!canGoPrev}
          >
            ‹
          </Chevron>
          <MonthText>{weekRangeText(weekStart)}</MonthText>
          <Chevron
            aria-label="Next available week"
            onClick={gotoNextAvailableWeek}
            disabled={!canGoNext}
          >
            ›
          </Chevron>
        </DaysBar>

        <WeekRow>
          {days.map((d) => {
            const disabled = isPastDay(d) || !hasWindows(d);
            const selected = selectedDate ? sameDay(d, selectedDate) : false;

            return (
              <DayCol key={d.toDateString()}>
                <DayCard
                  $selected={selected}
                  $disabled={disabled}
                  onClick={() => {
                    if (disabled) return;
                    setSelectedDate(d);
                    setSelectedStart(null);
                  }}
                >
                  <DayName $selected={selected}>{dowShort(d)}</DayName>
                  <DayNum $selected={selected}>{d.getDate()}</DayNum>
                </DayCard>
                {/* removed mini-times below day cards */}
              </DayCol>
            );
          })}
        </WeekRow>

        {/* TZ note */}
        <TZNote>Times shown in your time zone ({tzLabel})</TZNote>

        {/* Times */}
        <TimesScroll>
          <TimesGrid>
            {selectedDate && daySlots.length === 0 && (
              <div
                style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  fontSize: 12,
                  color: '#B7BDCB',
                }}
              >
                No times available for this day.
              </div>
            )}
            {selectedDate &&
              daySlots.map((t) => {
                const label = `${pad(t.getHours())}:${pad(t.getMinutes())}`;
                const sel = selectedStart ? +t === +selectedStart : false;
                return (
                  <TimeBtn key={+t} $selected={sel} onClick={() => setSelectedStart(t)}>
                    {label}
                  </TimeBtn>
                );
              })}
          </TimesGrid>
        </TimesScroll>

        {/* Footer */}
        <Footer>
          <Hint>Chris will confirm your request and send details by email.</Hint>
          <ConfirmBtn
            disabled={!selectedDate || !selectedStart}
            onClick={() => {
              if (!selectedDate || !selectedStart) return;
              // submit here...
              onOpenChange(false);
            }}
          >
            Confirm Meeting
          </ConfirmBtn>
          <CancelBtn onClick={onCancelAll}>Cancel</CancelBtn>
        </Footer>

        {/* Safe area spacer (extra 40px + safe area) */}
        <SafeAreaBottom />
      </Panel>
    </Dialog.Root>
  );
}
